import { Icon } from '../../../components/index.js';

export interface CosmeticThumbnailOption {
  id: string;
  name: string;
  asset: string;
  unlocked: boolean;
}

export interface CosmeticThumbnailPickerProps {
  legend: string;
  options: readonly CosmeticThumbnailOption[];
  value: string | null;
  onChange: (next: string | null) => void;
  /** `square` for avatar frames (the art is already ring-shaped with a transparent centre); `landscape` for backgrounds/decorations. */
  aspect: 'square' | 'landscape';
  fit?: 'contain' | 'cover';
  /** Lifted to the parent so at most one picker is expanded on mobile at a time. */
  mobileOpen: boolean;
  onMobileToggle: () => void;
}

function ThumbnailTile({
  name,
  asset,
  selected,
  locked,
  onSelect,
  aspect,
  fit = 'contain',
}: {
  name: string;
  asset?: string;
  selected: boolean;
  locked: boolean;
  onSelect: () => void;
  aspect: 'square' | 'landscape';
  fit?: 'contain' | 'cover';
}) {
  return (
    <button
      type="button"
      disabled={locked}
      aria-pressed={locked ? undefined : selected}
      onClick={onSelect}
      className={`interactive-lift relative overflow-hidden rounded-md border bg-surface text-left transition-colors duration-fast ease-standard disabled:cursor-not-allowed disabled:hover:border-border ${
        selected && !locked
          ? 'border-accent bg-brand/10 shadow-1'
          : 'border-border hover:border-borderStrong'
      }`}
    >
      <span
        className={`flex w-full items-center justify-center overflow-hidden bg-inset ${
          aspect === 'square' ? 'aspect-square' : 'aspect-[4/3]'
        }`}
      >
        {asset ? (
          <img
            src={asset}
            alt=""
            aria-hidden="true"
            draggable={false}
            className={`h-full w-full ${fit === 'cover' ? 'object-cover' : 'object-contain p-1.5'} ${
              locked ? 'opacity-60 grayscale' : ''
            }`}
          />
        ) : (
          <span
            aria-hidden="true"
            className="h-1/2 w-1/2 rounded-full border-2 border-dashed border-borderStrong/60"
          />
        )}
      </span>
      {selected && !locked && (
        <span
          aria-hidden="true"
          className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full border border-accent bg-brand text-textOnBrand shadow-1"
        >
          <Icon name="check" className="h-3 w-3" />
        </span>
      )}
      {locked && (
        <span
          aria-hidden="true"
          className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full border border-borderStrong bg-surface/90 text-textSecondary shadow-1"
        >
          <Icon name="lock" className="h-3 w-3" />
        </span>
      )}
      <span className="block truncate px-1.5 py-1.5 text-xs font-semibold text-textPrimary">
        {name}
        {selected && !locked && <span className="sr-only"> (selected)</span>}
        {locked && <span className="sr-only"> (locked)</span>}
      </span>
    </button>
  );
}

/**
 * A visual thumbnail grid for one cosmetic slot (avatar frame / hero
 * background / side decoration) — one generic component reused for all
 * three rather than three near-duplicate ones (APP_SPEC §13.2).
 *
 * Locked options are shown, not filtered out: a disabled button with a
 * grayscale thumbnail and a lock badge, so the player can see what exists
 * without being able to select it (the server independently enforces this
 * regardless — `modules/users/profile-cosmetics.ts`). Selected state is
 * never color-only: a gold ring plus a checkmark badge plus visually-hidden
 * text.
 *
 * Below `tablet:` this collapses to a single summary row (current selection
 * + chevron) that expands the same grid in place on tap — no new modal/sheet
 * framework, matching the app's existing patterns.
 */
export function CosmeticThumbnailPicker({
  legend,
  options,
  value,
  onChange,
  aspect,
  fit = 'contain',
  mobileOpen,
  onMobileToggle,
}: CosmeticThumbnailPickerProps) {
  const selectedOption = value ? options.find((o) => o.id === value) : undefined;
  const selectedName = selectedOption?.name ?? 'None';

  const grid = (
    <div
      className={`grid gap-3 ${aspect === 'square' ? 'grid-cols-3 tablet:grid-cols-5' : 'grid-cols-2 tablet:grid-cols-3'}`}
    >
      <ThumbnailTile
        name="None"
        selected={value === null}
        locked={false}
        onSelect={() => onChange(null)}
        aspect={aspect}
      />
      {options.map((option) => (
        <ThumbnailTile
          key={option.id}
          name={option.name}
          asset={option.asset}
          fit={fit}
          selected={value === option.id}
          locked={!option.unlocked}
          onSelect={() => onChange(option.id)}
          aspect={aspect}
        />
      ))}
    </div>
  );

  return (
    <fieldset className="min-w-0">
      <legend className="text-sm font-semibold text-textPrimary">{legend}</legend>

      <div className="mt-2 tablet:hidden">
        <button
          type="button"
          onClick={onMobileToggle}
          aria-expanded={mobileOpen}
          className="flex min-h-touch w-full items-center gap-3 rounded-md border border-border bg-surface px-3 py-2 text-left transition-colors duration-fast ease-standard hover:bg-surfaceElevated"
        >
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden bg-inset ${aspect === 'square' ? 'rounded-full' : 'rounded-md'}`}
          >
            {selectedOption ? (
              <img
                src={selectedOption.asset}
                alt=""
                aria-hidden="true"
                className={`h-full w-full ${fit === 'cover' ? 'object-cover' : 'object-contain p-1'}`}
              />
            ) : (
              <span
                aria-hidden="true"
                className="h-5 w-5 rounded-full border-2 border-dashed border-borderStrong/60"
              />
            )}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-textPrimary">
            {selectedName}
          </span>
          <Icon
            name="arrowRight"
            className={`h-4 w-4 shrink-0 text-textSecondary transition-transform duration-fast ${mobileOpen ? 'rotate-90' : ''}`}
          />
        </button>
        {mobileOpen && <div className="mt-3">{grid}</div>}
      </div>

      <div className="mt-2 hidden tablet:block">{grid}</div>
    </fieldset>
  );
}
